import { useEffect, type RefObject } from 'react'
import { cn } from '@/lib/utils'

/**
 * Pencil marks that attach to the landing content and draw themselves as you scroll.
 * Each mark is a few strokes drawn in order, like a pen; the faint second pass gives
 * the doubled line of a pencil. Scroll position is written straight to the DOM.
 */

/** `spin` turns the stroke around that point as the page scrolls. */
type Stroke = { d: string; accent?: boolean; spin?: { x: number; y: number } }

type SketchProps = {
  strokes: Stroke[]
  viewBox: string
  className?: string
  /** Viewport fractions: drawing starts when the mark's top reaches `from`, ends at `to`. */
  from?: number
  to?: number
  /** Draw once on load over this many milliseconds, instead of following the scroll. */
  onLoad?: number
  stretch?: boolean
}

function Sketch({ strokes, viewBox, className, from = 0.86, to = 0.42, onLoad, stretch }: SketchProps) {
  return (
    <svg
      className={cn('yeshuman-sketch', className)}
      viewBox={viewBox}
      preserveAspectRatio={stretch ? 'none' : undefined}
      fill="none"
      aria-hidden
      data-from={from}
      data-to={to}
      data-onload={onLoad}
    >
      {strokes.map((stroke, i) => (
        <g
          key={stroke.d}
          className={cn(stroke.accent && 'yeshuman-sketch-accent', stroke.spin && 'yeshuman-sketch-spin')}
          style={stroke.spin ? { transformOrigin: `${stroke.spin.x}px ${stroke.spin.y}px` } : undefined}
        >
          <path d={stroke.d} pathLength={1} data-order={i} />
          <path d={stroke.d} pathLength={1} data-order={i} className="yeshuman-sketch-echo" />
        </g>
      ))}
    </svg>
  )
}

const ring = (cx: number, cy: number, r: number) =>
  `M${cx - r} ${cy} a${r} ${r} 0 1 1 ${2 * r} 0 a${r} ${r} 0 1 1 ${-2 * r} 0`

/** Short pieces of an arc, for the board's dashed construction circles. */
function dashedArc(cx: number, cy: number, r: number, fromDeg: number, toDeg: number, step = 11) {
  let d = ''
  for (let a = fromDeg; a < toDeg; a += step) {
    const point = (deg: number) => {
      const t = (deg * Math.PI) / 180
      return `${(cx + r * Math.cos(t)).toFixed(1)} ${(cy + r * Math.sin(t)).toFixed(1)}`
    }
    d += `M${point(a)} A${r} ${r} 0 0 1 ${point(a + step * 0.55)} `
  }
  return d
}

/** The circle and crosshair that open and close the page. */
function instrument(cx: number, cy: number, r: number): Stroke[] {
  const b = r * 1.32
  return [
    { d: ring(cx, cy, r) },
    { d: ring(cx + 1, cy - 1, r - 2.5) },
    { d: ring(cx, cy, 4), accent: true },
    {
      d: `M${cx + r * 0.39} ${cy - r * 1.08} A${r * 1.15} ${r * 1.15} 0 0 1 ${cx + r * 1.1} ${cy - r * 0.3}`,
      accent: true,
      spin: { x: cx, y: cy },
    },
    { d: dashedArc(cx, cy, r * 1.15, 100, 215), spin: { x: cx, y: cy } },
    { d: `M${cx - b + 12} ${cy - b} H${cx - b} V${cy - b + 46}` },
    { d: `M${cx + b - 12} ${cy + b} H${cx + b} V${cy + b - 46}` },
  ]
}

export function HeroMark() {
  return (
    <Sketch
      className="yeshuman-mark-hero"
      viewBox="0 0 300 360"
      onLoad={5200}
      strokes={[
        { d: 'M14 150 L286 151' },
        { d: 'M150 12 L151 240' },
        ...instrument(150, 150, 100),
        { d: 'M150 262 V276 M150 290 V304 M150 318 V332 M150 346 V360' },
      ]}
    />
  )
}

export function OverlapMark() {
  return (
    <Sketch
      className="yeshuman-mark-overlap"
      viewBox="0 0 340 270"
      strokes={[
        { d: 'M2 20 L232 18 M212 4 L213 180 M226 164 L4 162 M20 178 L21 2' },
        { d: 'M74 96 L322 94 M304 78 L305 264 M318 248 L76 246 M92 262 L93 80', accent: true },
        {
          d: 'M212 112 L170 154 M212 124 L182 154 M212 136 L194 154 M212 100 L158 154 M204 96 L146 154 M190 96 L134 152',
          accent: true,
        },
      ]}
    />
  )
}

/** A circle around the step number, and a line running on to an open node. */
export function StepMark({ index }: { index: number }) {
  const from = 0.9 - index * 0.08
  return (
    <>
      <Sketch
        className="yeshuman-mark-ring"
        viewBox="0 0 56 56"
        strokes={[{ d: ring(28, 28, 24), accent: true }, { d: ring(28.5, 27.5, 22.5), accent: true }]}
        from={from}
        to={from - 0.12}
      />
      <Sketch
        className="yeshuman-mark-tail"
        viewBox="0 0 400 12"
        stretch
        strokes={[{ d: 'M0 6 L400 6' }]}
        from={from - 0.1}
        to={from - 0.24}
      />
      <Sketch
        className="yeshuman-mark-node"
        viewBox="0 0 14 14"
        strokes={[{ d: ring(7, 7, 5) }]}
        from={from - 0.24}
        to={from - 0.28}
      />
    </>
  )
}

export function NameUnderline() {
  return (
    <Sketch
      className="yeshuman-mark-name"
      viewBox="0 0 320 12"
      stretch
      strokes={[{ d: 'M0 6 L320 6', accent: true }]}
      from={0.84}
      to={0.68}
    />
  )
}

/** The line under the headline's last word, drawn on load. */
export function HeadlineUnderline() {
  return (
    <Sketch
      className="yeshuman-mark-headline"
      viewBox="0 0 300 20"
      stretch
      onLoad={2600}
      strokes={[{ d: 'M2 12 C50 5,104 15,152 9 S248 5,298 11', accent: true }]}
    />
  )
}

/** A drawn rule between the two Who we help items. */
export function SectionRule() {
  return (
    <Sketch
      className="yeshuman-mark-rule"
      viewBox="0 0 600 10"
      stretch
      strokes={[{ d: 'M0 5 C160 3,380 7,600 5' }]}
      from={0.8}
      to={0.5}
    />
  )
}

/** Two open corners around the promise: lighter than a full frame. */
export function PromiseMark() {
  return (
    <Sketch
      className="yeshuman-mark-promise"
      viewBox="0 0 1000 200"
      stretch
      from={0.82}
      to={0.5}
      strokes={[
        { d: 'M600 6 L996 5' },
        { d: 'M984 -8 L985 118' },
        { d: 'M400 194 L4 195' },
        { d: 'M16 208 L15 88' },
        { d: 'M7 100 L15 84 L23 100', accent: true },
      ]}
    />
  )
}

export function CloseMark() {
  return (
    <Sketch
      className="yeshuman-mark-close"
      viewBox="0 0 460 560"
      from={0.96}
      to={0.2}
      strokes={[
        { d: 'M422 0 L421 250 C421 340,380 398,300 400' },
        ...instrument(190, 400, 110),
        { d: 'M190 262 L191 538' },
        { d: 'M300 400 L58 401' },
        { d: 'M44 401 H30 M18 401 H4 M-8 401 H-22' },
        { d: 'M-10 394 L-23 401 L-10 408', accent: true },
      ]}
    />
  )
}

/** Grain and a slight wander, so strokes read as pencil on paper. Render once on the page. */
export function PencilFilter() {
  return (
    <svg width="0" height="0" className="absolute" aria-hidden>
      <filter id="yeshuman-pencil" x="-15%" y="-15%" width="130%" height="130%">
        <feTurbulence type="fractalNoise" baseFrequency="0.018" numOctaves="2" seed="5" result="wander" />
        <feDisplacementMap in="SourceGraphic" in2="wander" scale="2.2" result="moved" />
        <feTurbulence type="fractalNoise" baseFrequency="1.1" numOctaves="2" seed="3" result="noise" />
        <feColorMatrix
          in="noise"
          type="matrix"
          values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -3 2.3"
          result="grain"
        />
        <feComposite in="moved" in2="grain" operator="in" />
      </filter>
    </svg>
  )
}

/** How far the outer arcs turn, in degrees, per pixel scrolled. */
const SPIN_PER_PIXEL = 0.08

function clamp(n: number) {
  return Math.min(1, Math.max(0, n))
}

function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node
  }
  return null
}

/**
 * Non-scaling strokes measure dashes on screen, while `pathLength` is measured in the
 * viewBox, so a stretched mark needs its dash scaled up or it stops short of the end.
 */
function dashScale(svg: SVGSVGElement) {
  const box = svg.getBoundingClientRect()
  const view = svg.viewBox.baseVal
  return Math.max(box.width / view.width, box.height / view.height) || 1
}

function paint(svg: SVGSVGElement, progress: number) {
  const paths = svg.querySelectorAll<SVGPathElement>('path')
  const count = paths.length / 2
  const dash = dashScale(svg)
  paths.forEach((path) => {
    const local = clamp(progress * count - Number(path.dataset.order))
    path.style.strokeDasharray = `${dash} ${dash}`
    path.style.strokeDashoffset = String((1 - local) * dash)
    path.style.visibility = local > 0 ? 'visible' : 'hidden'
  })
}

/** Draws every mark under `rootRef` in step with the scroll position. */
export function useSketchMarks(rootRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const svgs = Array.from(root.querySelectorAll<SVGSVGElement>('.yeshuman-sketch'))
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      svgs.forEach((svg) => paint(svg, 1))
      return
    }

    const scroller = scrollParent(root)
    const shown = new Map(svgs.map((svg) => [svg, 0]))
    const spinners = Array.from(root.querySelectorAll<SVGGElement>('.yeshuman-sketch-spin'))
    let spin = 0
    const loadStart = performance.now()
    let frame = 0

    const targetFor = (svg: SVGSVGElement, atEnd: boolean, now: number) => {
      if (svg.dataset.onload) return clamp((now - loadStart - 300) / Number(svg.dataset.onload))
      if (atEnd) return 1
      const vh = window.innerHeight
      const from = Number(svg.dataset.from) * vh
      const to = Number(svg.dataset.to) * vh
      return clamp((from - svg.getBoundingClientRect().top) / (from - to))
    }

    const tick = (now: number) => {
      frame = 0
      const view = scroller ?? document.documentElement
      const atEnd = view.scrollTop + view.clientHeight >= view.scrollHeight - 4
      let moving = false
      for (const svg of svgs) {
        const target = targetFor(svg, atEnd, now)
        const current = shown.get(svg) ?? 0
        const next = Math.abs(target - current) < 0.002 ? target : current + (target - current) * 0.07
        if (next !== current) {
          shown.set(svg, next)
          paint(svg, next)
        }
        if (next !== target || (svg.dataset.onload && target < 1)) moving = true
      }
      const spinTarget = view.scrollTop * SPIN_PER_PIXEL
      if (Math.abs(spinTarget - spin) > 0.05) {
        spin += (spinTarget - spin) * 0.06
        spinners.forEach((group) => {
          group.style.transform = `rotate(${spin}deg)`
        })
        moving = true
      }
      if (moving) frame = requestAnimationFrame(tick)
    }

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(tick)
    }

    svgs.forEach((svg) => paint(svg, 0))
    schedule()
    const target: HTMLElement | Window = scroller ?? window
    target.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(frame)
      target.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [rootRef])
}
